/** @file EDITOR_SETTINGS: stored settings (URL, keys, theme, defaults, startup) in localStorage. */
import type { Binding } from '@zilo/pose3d-shortcuts/types';
import type { NewPuppetDraft } from '../screens/newPuppetTypes';
import type { TileRef } from '../state/editorTypes';
import { localSetting } from './localSetting';
import type { LocalSetting } from './localSetting';

/** The colour theme: `dark` (default), `darker` (near black), `light` (a stub: only the main surfaces are restyled). */
export type ThemeName = 'dark' | 'darker' | 'light';

/** The scope and profile Settings preselect in the New puppet modal (null = no default; the human still chooses). */
export type NewPuppetDefaults = Pick<NewPuppetDraft, 'directions' | 'pitch' | 'camera' | 'profile'>;

/** The export options Settings preselect: bake directions ('' = the puppet's facings), scale, and the default format. */
export type ExportDefaults = { directions: string; scale: number; format: 'bundle' | 'baked' };

/** The interface language; only English has strings today (`th` is reserved for the translation). */
export type LanguageName = 'en' | 'th';

/**
 * `url`: the server to talk to (empty = this page's origin, proxied by Vite); `shortcuts`: binding overrides;
 * `theme`, `newPuppet`, `export`, `pixelGrid` (the ground grid at start), `language`; `reopenLast` + `lastRoute`:
 * open the last puppet screen again when the editor starts without a link; `tiles(puppet)`: that puppet's open Tile
 * tabs (`pose3d.tiles.<puppet>`), restored when it opens.
 */
export const EDITOR_SETTINGS = {
  url: localSetting<string>('pose3d.url', ''),
  shortcuts: localSetting<Binding[]>('pose3d.shortcuts', []),
  theme: localSetting<ThemeName>('pose3d.theme', 'dark'),
  newPuppet: localSetting<NewPuppetDefaults>('pose3d.newPuppet', { directions: null, pitch: null, camera: null, profile: null }),
  export: localSetting<ExportDefaults>('pose3d.export', { directions: '', scale: 1, format: 'bundle' }),
  pixelGrid: localSetting<boolean>('pose3d.pixelGrid', true),
  language: localSetting<LanguageName>('pose3d.language', 'en'),
  reopenLast: localSetting<boolean>('pose3d.reopenLast', false),
  lastRoute: localSetting<string | null>('pose3d.lastRoute', null),
  /** The open Tile tabs of one puppet. */
  tiles: (puppetId: string): LocalSetting<TileRef[]> => localSetting<TileRef[]>(`pose3d.tiles.${puppetId}`, []),
};
